/**
 * Hangul to English conversion utility
 * Used for auto-converting Korean input in password fields
 */

const CHO_HANGUL = [
  'r', 'R', 's', 'e', 'E', 'f', 'a', 'q', 'Q', 't', 'T', 'd', 'w', 'W', 'c', 'z', 'x', 'v', 'g'
];

const JUNG_HANGUL = [
  'k', 'o', 'i', 'O', 'j', 'p', 'u', 'P', 'h', 'hk', 'ho', 'hl', 'y', 'n', 'nj', 'np', 'nl', 'b', 'm', 'ml', 'l'
];

const JONG_HANGUL = [
  '', 'r', 'R', 'rt', 's', 'sw', 'sg', 'e', 'f', 'fr', 'fa', 'fq', 'ft', 'fx', 'fv', 'fg', 'a', 'q', 'qt', 't', 'T', 'd', 'w', 'c', 'z', 'x', 'v', 'g'
];

const JAMO_MAP: Record<string, string> = {
  // Consonants (Ja-eum)
  'ㄱ': 'r', 'ㄲ': 'R', 'ㄳ': 'rt', 'ㄴ': 's', 'ㄵ': 'sw', 'ㄶ': 'sg', 'ㄷ': 'e', 'ㄸ': 'E', 'ㄹ': 'f', 
  'ㄺ': 'fr', 'ㄻ': 'fa', 'ㄼ': 'fq', 'ㄽ': 'ft', 'ㄾ': 'fx', 'ㄿ': 'fv', 'ㅀ': 'fg', 'ㅁ': 'a', 
  'ㅂ': 'q', 'ㅃ': 'Q', 'ㅄ': 'qt', 'ㅅ': 't', 'ㅆ': 'T', 'ㅇ': 'd', 'ㅈ': 'w', 'ㅉ': 'W', 'ㅊ': 'c', 
  'ㅋ': 'z', 'ㅌ': 'x', 'ㅍ': 'v', 'ㅎ': 'g',
  
  // Vowels (Mo-eum)
  'ㅏ': 'k', 'ㅐ': 'o', 'ㅑ': 'i', 'ㅒ': 'O', 'ㅓ': 'j', 'ㅔ': 'p', 'ㅕ': 'u', 'ㅖ': 'P', 'ㅗ': 'h', 
  'ㅘ': 'hk', 'ㅙ': 'ho', 'ㅚ': 'hl', 'ㅛ': 'y', 'ㅜ': 'n', 'ㅝ': 'nj', 'ㅞ': 'np', 'ㅟ': 'nl', 'ㅠ': 'b', 
  'ㅡ': 'm', 'ㅢ': 'ml', 'ㅣ': 'l'
};

/**
 * Converts a string containing Korean characters to their QWERTY English counterparts.
 * Non-Korean characters are returned as is.
 * 
 * @param text Input string
 * @returns Converted string
 */
export function convertHangulToEnglish(text: string): string {
  let result = '';
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (!char) continue;
    
    const code = char.charCodeAt(0);

    // Check for Hangul Syllables (AC00-D7A3)
    if (code >= 0xAC00 && code <= 0xD7A3) {
      const syllableIndex = code - 0xAC00;
      const choIndex = Math.floor(syllableIndex / 588);
      const jungIndex = Math.floor((syllableIndex % 588) / 28);
      const jongIndex = syllableIndex % 28;

      result += (CHO_HANGUL[choIndex] || '') + 
                (JUNG_HANGUL[jungIndex] || '') + 
                (JONG_HANGUL[jongIndex] || '');
      continue;
    }

    // Check for Hangul Compatibility Jamo (3130-318F) - for incomplete syllables
    if (code >= 0x3131 && code <= 0x318E) {
      result += JAMO_MAP[char] || char;
      continue;
    }

    // Pass through other characters
    result += char;
  }
  
  return result;
}
